import * as THREE from 'three';
import { invalidateEnvMapCache } from './env-map-loader';

export const SCENE_ASSET_REPLACED = 'scene-asset-replaced';

export type SceneAssetReplacedDetail = {
  oldUrl: string;
  newUrl: string;
  assetId?: string;
};

const textureUrlClearHooks: Array<(oldUrl: string) => void> = [];

/** Register hooks that drop URL-keyed texture caches (glb-model, material-graph-eval, …). */
export function registerAssetUrlTextureClearHook(fn: (oldUrl: string) => void): void {
  textureUrlClearHooks.push(fn);
}

function stripUrlQuery(url: string): string {
  return url.split('?')[0].split('#')[0];
}

function urlsMatch(a: string, b: string): boolean {
  return a === b || stripUrlQuery(a) === stripUrlQuery(b);
}

/** Drop cached env / texture data for a replaced upload URL and notify the live scene. */
export function notifySceneAssetReplaced(detail: SceneAssetReplacedDetail): void {
  invalidateEnvMapCache(detail.oldUrl);
  if (detail.newUrl && !urlsMatch(detail.oldUrl, detail.newUrl)) {
    invalidateEnvMapCache(detail.newUrl);
  }

  for (const fn of textureUrlClearHooks) {
    try {
      fn(detail.oldUrl);
    } catch {
      /* ignore */
    }
  }

  if (typeof document !== 'undefined') {
    document.dispatchEvent(new CustomEvent(SCENE_ASSET_REPLACED, { detail }));
  }
}

export function textureCacheKeyUsesUrl(cacheKey: string, url: string): boolean {
  const base = stripUrlQuery(url);
  return cacheKey === url || cacheKey.startsWith(`${url}__`) || cacheKey.startsWith(`${base}__`);
}

export function disposeTexturesInMapByUrl(
  cache: Map<string, THREE.Texture>,
  url: string,
): void {
  for (const [key, tex] of [...cache.entries()]) {
    if (!textureCacheKeyUsesUrl(key, url)) continue;
    tex.dispose();
    cache.delete(key);
  }
}
